import { useState, useEffect, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { UserCircle2, Users, Loader2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { OnlineUser } from "@/services/admin/adminService";
import { useAdmin } from "@/hooks/useAdmin";
import { adminLabels } from "@/i18n";
import { getLanguage } from "@/utils/languageUtils";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

import { useNativePlatform } from "@/hooks/useNativePlatform";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

interface OnlineUsersDropdownProps {
  /** When true, badge has no bottom margin (for use inside AdminStatsBar). */
  compact?: boolean;
}

export const OnlineUsersDropdown = ({ compact }: OnlineUsersDropdownProps) => {
  const adminLabels_i18n = adminLabels(getLanguage());
  const { isNative } = useNativePlatform();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadedUsers, setLoadedUsers] = useState<OnlineUser[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Fetch count with polling every 30 seconds via useAdmin hook (optimized - only count)
  const { onlineUsersCount, fetchOnlineUsersPage } = useAdmin();
  const activeUsers = onlineUsersCount.data || 0;
  const [isLoadingFirstPage, setIsLoadingFirstPage] = useState(false);
  const isFetchingRef = useRef(false);
  const prevIsOpenRef = useRef(false);

  // Fetch first page when dropdown opens - always fetch fresh data
  useEffect(() => {
    // Only fetch when dropdown changes from closed to open
    if (isOpen && !prevIsOpenRef.current && !isFetchingRef.current) {
      isFetchingRef.current = true;
      setIsLoadingFirstPage(true);
      // Reset state
      setCurrentPage(1);
      setLoadedUsers([]);
      setHasMore(false);

      // Invalidate cache to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ["onlineUsers"] });

      // Fetch fresh data
      fetchOnlineUsersPage(1, 25)
        .then((data) => {
          setLoadedUsers(data.users);
          setHasMore(data.hasMore);
        })
        .catch((error) => {})
        .finally(() => {
          setIsLoadingFirstPage(false);
          isFetchingRef.current = false;
        });
    }

    // Update previous open state
    prevIsOpenRef.current = isOpen;

    // Reset fetching flag when dropdown closes
    if (!isOpen) {
      isFetchingRef.current = false;
    }
  }, [isOpen, fetchOnlineUsersPage, queryClient]);

  const handleLoadMore = async () => {
    setIsLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      // Fetch next page via useAdmin hook
      const response = await fetchOnlineUsersPage(nextPage, 25);

      setLoadedUsers((prev) => [...prev, ...response.users]);
      setCurrentPage(nextPage);
      setHasMore(response.hasMore);
    } catch (error) {
    } finally {
      setIsLoadingMore(false);
    }
  };

  const userListBody = (
    <div className="p-2 space-y-1">
      {isLoadingFirstPage ? (
        <div className="px-2 py-1 text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          {adminLabels_i18n.loading}
        </div>
      ) : loadedUsers.length === 0 ? (
        <div className="px-2 py-1 text-sm text-muted-foreground">
          {adminLabels_i18n.noOnlineUsers}
        </div>
      ) : (
        <>
          {loadedUsers.map((user) => (
            <div
              key={user.id}
              className="flex items-center gap-2 px-2 py-1 text-sm rounded-sm hover:bg-accent/20"
            >
              <UserCircle2 className="w-4 h-4 text-green-500" />
              <div className="flex flex-col">
                <span>{user.name}</span>
                <span className="text-xs text-muted-foreground">
                  {user.email}
                </span>
              </div>
            </div>
          ))}
          {hasMore && (
            <div className="pt-2 border-t">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-center"
                onClick={handleLoadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {adminLabels_i18n.loading}
                  </>
                ) : (
                  adminLabels_i18n.showMore
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );

  const triggerBadge = (
    <Badge
      variant="secondary"
      className={cn(
        "bg-green-500/10 text-green-500 border-green-500/20 cursor-pointer hover:bg-green-500/20 transition-colors",
        !compact && "mb-4",
        compact && "text-[9px] sm:text-xs py-0.5 px-1 sm:py-1 sm:px-2",
      )}
    >
      <UserCircle2
        className={cn(
          compact
            ? "w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1"
            : "w-3 h-3 mr-1",
        )}
      />
      {onlineUsersCount.isLoading
        ? adminLabels_i18n.loading
        : `${activeUsers} ${adminLabels_i18n.online}`}
    </Badge>
  );

  if (isNative) {
    return (
      <>
        <Button
          variant="ghost"
          type="button"
          className="h-auto p-0 hover:bg-transparent backdrop-blur"
          onClick={() => setIsOpen(true)}
        >
          {triggerBadge}
        </Button>
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetContent side="bottom" className="max-h-[70dvh] overflow-y-auto">
            <SheetHeader className="text-left">
              <SheetTitle className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                {adminLabels_i18n.loggedInUsers}
              </SheetTitle>
            </SheetHeader>
            <div className="mt-4">{userListBody}</div>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-auto p-0 hover:bg-transparent backdrop-blur"
        >
          {triggerBadge}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-64 max-h-96 overflow-y-auto"
      >
        <div className="px-2 py-1.5 text-sm font-semibold">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            {adminLabels_i18n.loggedInUsers}
          </div>
        </div>
        {userListBody}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
